import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Global request queue to prevent rapid requests
let requestQueue = new Map();
const REQUEST_DEBOUNCE_MS = 1000; // 1 second debounce for same requests

/** Admin-only "view as" allowed emails (must be lowercase). */
export const ADMIN_VIEW_AS_EMAILS = [
  'info@showstop.ai',
  'anneliese@showstop.ai',
  'hello@picklematch.co'
];

/** When admin selects "All", no userId is sent so backend returns all trends/briefs/videos. */
export const ADMIN_VIEW_ALL = '__ADMIN_VIEW_ALL__';

const ADMIN_ACT_AS_KEY = 'adminActAsEmail';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Global auth state to prevent multiple instances
let globalAuthState = {
  isChecking: false,
  lastCheckTime: 0,
  checkPromise: null
};

// Configure axios defaults
axios.defaults.withCredentials = true; // Send cookies with all requests

// Set up axios interceptor to handle rate limiting globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      console.warn('⚠️ Rate limited on request:', error.config?.url);
      // Don't show toast for every 429, just log it
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState(() => localStorage.getItem('selectedAccountId'));
  const [selectedAccountName, setSelectedAccountName] = useState(() => localStorage.getItem('selectedAccountName'));
  const [actAsEmail, setActAsEmailState] = useState(() => {
    const stored = localStorage.getItem(ADMIN_ACT_AS_KEY);
    if (!stored) return null;
    return stored === ADMIN_VIEW_ALL || ADMIN_VIEW_AS_EMAILS.includes(stored) ? stored : null;
  });

  // Set up axios defaults and check authentication
  useEffect(() => {
    const checkAuth = async () => {
      // Prevent multiple simultaneous auth checks using global state
      const now = Date.now();
      const timeSinceLastCheck = now - globalAuthState.lastCheckTime;
      
      // If we're already checking or checked recently (within 10 seconds), skip
      if (globalAuthState.isChecking || (timeSinceLastCheck < 10000 && globalAuthState.checkPromise)) {
        console.log('⏭️ AuthContext: Auth check already in progress or too recent, skipping...');
        if (globalAuthState.checkPromise) {
          try {
            await globalAuthState.checkPromise;
          } catch (error) {
            console.log('Previous auth check failed, continuing...');
          }
        }
        return;
      }
      
      globalAuthState.isChecking = true;
      globalAuthState.lastCheckTime = now;
      
      // Store the promise so other instances can wait for it
      globalAuthState.checkPromise = (async () => {
        const sessionToken = localStorage.getItem('sessionToken');
        const storedAccountId = localStorage.getItem('selectedAccountId');
        const storedAccountName = localStorage.getItem('selectedAccountName');
        
        console.log('🔍 AuthContext: Checking authentication...', { 
          hasSessionToken: !!sessionToken, 
          storedAccountId, 
          storedAccountName 
        });
        
        // Check auth status (cookies will be sent automatically)
        try {
          console.log('📡 AuthContext: Checking auth status with server...');
          const response = await axios.get('/api/auth/status', {
            timeout: 10000, // 10 second timeout
            headers: {
              'Cache-Control': 'no-cache',
              ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
            },
            withCredentials: true
          });
          console.log('📡 AuthContext: Server response:', response.data);
          
          if (response.data.authenticated && response.data.user) {
            console.log('✅ AuthContext: Authentication successful');
            setUser(response.data.user);
            setIsAuthenticated(true);
            // Store session token if provided
            if (response.data.sessionToken) {
              localStorage.setItem('sessionToken', response.data.sessionToken);
            }
            // Restore selected account ID and name if available
            if (storedAccountId) {
              setSelectedAccountId(storedAccountId);
              console.log('🏢 AuthContext: Restored account ID:', storedAccountId);
            }
            if (storedAccountName) {
              setSelectedAccountName(storedAccountName);
              console.log('🏢 AuthContext: Restored account name:', storedAccountName);
            }
          } else {
            console.log('❌ AuthContext: Not authenticated');
            localStorage.removeItem('sessionToken');
            localStorage.removeItem('selectedAccountId');
            localStorage.removeItem('selectedAccountName');
          }
        } catch (error) {
          console.error('❌ AuthContext: Auth status check error:', error);
          
          // Handle rate limiting specifically
          if (error.response?.status === 429) {
            console.warn('⚠️ AuthContext: Rate limited, will retry later');
            // Don't clear session on rate limit, just wait longer
            globalAuthState.lastCheckTime = now + 60000; // Wait 60 seconds before next attempt
            // Don't clear the loading state immediately, let it retry
            setTimeout(() => {
              setLoading(false);
              globalAuthState.isChecking = false;
              globalAuthState.checkPromise = null;
            }, 2000);
            return;
          } else {
            // Clear session on other errors
            localStorage.removeItem('sessionToken');
            localStorage.removeItem('selectedAccountId');
            localStorage.removeItem('selectedAccountName');
          }
        }
        
        // Small delay to ensure UI has time to render
        setTimeout(() => {
          setLoading(false);
          globalAuthState.isChecking = false;
          globalAuthState.checkPromise = null;
          console.log('🏁 AuthContext: Initial auth check complete');
        }, 100);
      })();
      
      // Wait for the auth check to complete
      await globalAuthState.checkPromise;
    };

    checkAuth();

    // Listen for storage changes (cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === 'sessionToken') {
        if (e.newValue) {
          // Session token was added in another tab
          // Only check auth if not already checking
          if (!globalAuthState.isChecking) {
            checkAuth();
          }
        } else {
          // Session token was removed in another tab
          setUser(null);
          setIsAuthenticated(false);
          setSelectedAccountId(null);
          setSelectedAccountName(null);
          setActAsEmailState(null);
          globalAuthState.isChecking = false;
          globalAuthState.checkPromise = null;
        }
      } else if (e.key === 'selectedAccountId') {
        setSelectedAccountId(e.newValue);
      } else if (e.key === 'selectedAccountName') {
        setSelectedAccountName(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const loginWithEmail = async (email, code) => {
    const normalizedLoginEmail = (email && String(email).trim().toLowerCase()) || '';
    try {
      const response = await axios.post('/api/auth/verify-code', {
        email,
        code
      }, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Cache-Control': 'no-cache'
        },
        withCredentials: true
      });

      if (response.data.success) {
        const { user, sessionToken } = response.data;
        if (sessionToken) {
          localStorage.setItem('sessionToken', sessionToken);
        }
        setUser(user);
        setIsAuthenticated(true);
        // Per-account login count (device) — Trend Spotter uses this to nudge brand settings from 3rd login onward
        const emailForKey = (user?.email && String(user.email).trim().toLowerCase()) || normalizedLoginEmail;
        if (emailForKey) {
          try {
            const k = `showstop_brand_settings_login_count_${emailForKey}`;
            const n = parseInt(localStorage.getItem(k) || '0', 10) + 1;
            localStorage.setItem(k, String(n));
          } catch (e) {
            console.warn('Brand settings login count: localStorage unavailable', e);
          }
        }
        try {
          sessionStorage.removeItem('showstop_brand_settings_prompt_dismissed');
        } catch (e) {
          console.warn('Brand settings prompt: sessionStorage unavailable', e);
        }
        toast.success('Login successful!');
        return true;
      } else {
        toast.error(response.data.error || 'Verification failed');
        return false;
      }
    } catch (error) {
      let message = 'Verification failed';
      
      if (error.response?.status === 429) {
        message = 'Too many requests. Please wait a moment and try again.';
        console.warn('⚠️ Verification rate limited');
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      } else if (error.code === 'ECONNABORTED') {
        message = 'Verification request timed out. Please try again.';
      }
      
      toast.error(message);
      return false;
    }
  };

  // Legacy login function for backward compatibility (deprecated)
  const login = async (email, password) => {
    console.warn('⚠️ login() is deprecated. Use loginWithEmail() instead.');
    return false;
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout', {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
    
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('selectedAccountId');
    localStorage.removeItem('selectedAccountName');
    localStorage.removeItem(ADMIN_ACT_AS_KEY);
    setUser(null);
    setIsAuthenticated(false);
    setSelectedAccountId(null);
    setSelectedAccountName(null);
    setActAsEmailState(null);
    toast.success('Logged out successfully');
  };

  const setActAsEmail = (email) => {
    if (email !== null && email !== ADMIN_VIEW_ALL && !ADMIN_VIEW_AS_EMAILS.includes(email)) return;
    setActAsEmailState(email);
    if (email) localStorage.setItem(ADMIN_ACT_AS_KEY, email);
    else localStorage.removeItem(ADMIN_ACT_AS_KEY);
  };

  const isAdmin = user?.email?.toLowerCase() === 'info@showstop.ai';
  const getEffectiveUserEmail = () => {
    if (isAdmin && actAsEmail === ADMIN_VIEW_ALL) return ''; // empty → backend returns all
    if (isAdmin && actAsEmail) return actAsEmail;
    return user?.email || null;
  };

  // Get current account ID with fallback
  const getAccountId = () => {
    return selectedAccountId || 'act_3630696143872779'; // fallback to default account
  };

  // Get current account name
  const getAccountName = () => {
    return selectedAccountName || 'Default Account'; // fallback to default name
  };

  // Get account display name in "Account X" format to minimize brand leakage
  const getAccountDisplayName = () => {
    if (!selectedAccountId) return 'Account 1';
    
    // Try to get account index from localStorage or use fallback
    const accountIndex = localStorage.getItem('selectedAccountIndex') || '1';
    return `Account ${accountIndex}`;
  };

  // Set account ID, name, and index, and persist them
  const setAccountId = (accountId, accountName = null, accountIndex = null) => {
    setSelectedAccountId(accountId);
    setSelectedAccountName(accountName);
    if (accountId) {
      localStorage.setItem('selectedAccountId', accountId);
    } else {
      localStorage.removeItem('selectedAccountId');
    }
    if (accountName) {
      localStorage.setItem('selectedAccountName', accountName);
    } else {
      localStorage.removeItem('selectedAccountName');
    }
    if (accountIndex) {
      localStorage.setItem('selectedAccountIndex', accountIndex);
    } else {
      localStorage.removeItem('selectedAccountIndex');
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithEmail,
    logout,
    selectedAccountId,
    selectedAccountName,
    setAccountId,
    getAccountId,
    getAccountName,
    getAccountDisplayName,
    actAsEmail,
    setActAsEmail,
    isAdmin,
    getEffectiveUserEmail,
    ADMIN_VIEW_AS_EMAILS,
    ADMIN_VIEW_ALL
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 